import { Check, Copy, Landmark, RefreshCcw } from "lucide-react";
import { useCallback, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaIconButton } from "@/shared/components/buttons/ColocaIconButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { PayinAmountWithFee } from "@/shared/components/payments/PayinAmountWithFee";
import { quickAmountsFor } from "@/shared/components/payments/payinQuickAmounts";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import { useOrgDepositAccounts } from "@/shared/hooks/useOrgDepositAccounts";
import useTranslations from "@/shared/lang/useTranslations";

import { usePayinAmountLabels } from "../hooks/usePayinAmountLabels";
import { useRuntimeDeposits } from "../hooks/useRuntimeDeposits";
import type { PayinRail } from "../services/paymentsPayin.service";
import { type PayinRuntimeEntity, PayinRuntimeStatus } from "../services/paymentsPayin.service";

/**
 * Generic panel for the PUSH-DETECTED way into a corridor: the organization publishes a standing receiving
 * account, anyone transfers to it for any amount, and the provider webhook attributes and credits the
 * deposit. This panel creates nothing — it shows the account (`GET /internal/payments/deposit-accounts`),
 * explains the flow, and lists the latest detected deposits.
 *
 * **Argentina is the only corridor that works this way**, and it is not a stylistic choice: the adapter
 * throws `does not support CBU_CVU payins`, so a charge cannot be created and a transfer to the standing CVU
 * is the only path in. The simulator is therefore on for ARG — with no form to price the money, this panel
 * has to answer "if they send X, what lands?".
 *
 * México used to render this panel too, on the belief that the organization held a standing CLABE. It does
 * not: Bitso leases a SPEI CLABE per charge from a pool shared across tenants, so MXN is charge-only
 * (`PayinMexicoForm`) and nothing here applies to it.
 *
 * Country-specific copy comes translated from the thin per-country wrapper (`ArgentinaDepositPanel`) so the
 * corridor keeps its own i18n keys without duplicating this UI.
 */

type DepositTone = "processing" | "credited" | "failed";

const STATUS_TONE: Record<PayinRuntimeStatus, DepositTone> = {
  [PayinRuntimeStatus.CREATED]: "processing",
  [PayinRuntimeStatus.PENDING]: "processing",
  [PayinRuntimeStatus.CONFIRMED]: "credited",
  [PayinRuntimeStatus.REJECTED]: "failed",
  [PayinRuntimeStatus.FAILED]: "failed",
  [PayinRuntimeStatus.REVERSED]: "failed",
};

const TONE_BADGE_VARIANT: Record<DepositTone, "secondary" | "success" | "destructive"> = {
  processing: "secondary",
  credited: "success",
  failed: "destructive",
};

function formatDepositAmount(deposit: PayinRuntimeEntity, locale: string): string {
  // Detected deposits carry the received amount; a not-yet-settled QR-initiated
  // payin only has the expected one — never render "0.00" for real money.
  const amount = Number(deposit.gross_amount_received ?? deposit.gross_amount_expected ?? 0);
  return `${amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${deposit.currency}`;
}

function formatDepositDate(iso: string, locale: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

export interface RuntimeDepositPanelTexts {
  instructionsTitle: string;
  instructionsBody: string;
  /** "Don't know your receiving account? Ask your account executive." hint. */
  accountHint: string;
  depositsTitle: string;
  depositsEmpty: string;
  refreshAria: string;
  statusProcessing: string;
  statusCredited: string;
  statusFailed: string;
}

export interface RuntimeDepositPanelProps {
  /** ISO 3166-1 alpha-3 country whose detected payins the panel lists (e.g. "MEX", "ARG"). */
  country: string;
  /** Rail of this corridor — decides which standing account to show and how to price the simulator. */
  rail: PayinRail;
  /** Currency of the corridor (e.g. "MXN", "ARS"). */
  currency: string;
  /**
   * Show the fee simulator. On for a corridor whose ONLY way in is push (Argentina), where there is no form
   * to price the money; off where a creation form already owns that job (México).
   */
  showSimulator?: boolean;
  /** Already-translated copy from the per-country wrapper. */
  texts: RuntimeDepositPanelTexts;
}

export function RuntimeDepositPanel({
  country,
  rail,
  currency,
  showSimulator = false,
  texts,
}: RuntimeDepositPanelProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const { user } = useAuth();
  const organizationId = user?.organization_id ?? user?.organization?.id ?? null;
  const { deposits, isLoading, hasAccess, hasError, refresh } = useRuntimeDeposits(country);

  // The simulator prices a real corridor: offering it where none is configured would show a fee for money
  // that cannot arrive. `isAvailable` fails open, so an unreadable catalog still renders it.
  const corridors = useAvailableCorridors();
  const corridorAvailable = corridors.isAvailable(rail, country, currency, "PAYIN");

  const { accounts } = useOrgDepositAccounts();
  const account = accounts.find((a) => a.country === country);

  const simulatorLabels = usePayinAmountLabels("simulator");
  const [copied, setCopied] = useState(false);

  const copyAccount = useCallback(() => {
    if (!account) return;
    void navigator.clipboard.writeText(account.account_number).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [account]);

  const toneLabel: Record<DepositTone, string> = {
    processing: texts.statusProcessing,
    credited: texts.statusCredited,
    failed: texts.statusFailed,
  };

  return (
    <div className="space-y-4">
      {/* How-to instructions — the receiving account itself is shared out-of-band by the account executive. */}
      <div className="space-y-2 rounded-xl border border-border bg-muted/40 p-4">
        <div className="flex items-center gap-2">
          <Landmark className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {texts.instructionsTitle}
          </p>
        </div>
        <p className="text-sm leading-relaxed text-foreground">{texts.instructionsBody}</p>

        {/* The number itself, when the runtime knows it. Falls back to the "ask your account executive"
            hint, which is all the panel could offer before the org-facing endpoint existed. */}
        {account ? (
          <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2">
            <div className="min-w-0">
              <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                {t("panel_account_title")} · {account.account_type}
              </p>
              {/* Breaks instead of truncating: a payer copies this number by hand, so it must read in full even
                  where "Copiar" leaves it less than its 22 digits' width. */}
              <p className="font-mono text-sm font-semibold tracking-wide break-all text-foreground">
                {account.account_number}
              </p>
            </div>
            <ColocaButton
              variant="ghost"
              size="sm"
              icon={copied ? Check : Copy}
              onClick={copyAccount}
              className="shrink-0"
            >
              {copied ? t("panel_account_copied") : t("panel_account_copy")}
            </ColocaButton>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">{texts.accountHint}</p>
        )}
      </div>

      {/* Fee simulator. Nothing is created here — this corridor credits whatever arrives at the standing
          account — so the control has no submit: it answers "if they send X, what lands?" and the reverse. */}
      {showSimulator && corridorAvailable ? (
        <div className="space-y-3 rounded-xl border border-border bg-card p-4">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("panel_simulator_title")}
          </p>
          <PayinAmountWithFee
            organizationId={organizationId}
            rail={rail}
            country={country}
            currency={currency}
            labels={simulatorLabels}
            quickAmounts={quickAmountsFor(currency)}
          />
        </div>
      ) : showSimulator ? (
        <p className="rounded-xl border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
          {t("panel_corridor_unavailable")}
        </p>
      ) : null}

      {/* Latest detected deposits — hidden entirely when the runtime could not be read (no fake data). */}
      {hasAccess && !hasError && (
        <div className="rounded-xl border border-border bg-card p-4">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              {texts.depositsTitle}
            </p>
            <ColocaIconButton
              icon={RefreshCcw}
              variant="ghost"
              size="sm"
              onClick={() => {
                void refresh();
              }}
              aria-label={texts.refreshAria}
            />
          </div>

          {isLoading ? (
            <div className="space-y-2" aria-hidden="true">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-1.5">
                  <div className="h-3.5 w-2/5 animate-pulse rounded bg-muted" />
                  <div className="h-3.5 w-16 animate-pulse rounded bg-muted" />
                </div>
              ))}
            </div>
          ) : deposits.length === 0 ? (
            <p className="py-2 text-sm text-muted-foreground">{texts.depositsEmpty}</p>
          ) : (
            <ul className="divide-y divide-border/60">
              {deposits.map((deposit) => {
                const tone = STATUS_TONE[deposit.status] ?? "processing";
                return (
                  <li key={deposit.id} className="flex items-center justify-between gap-3 py-2">
                    <div className="min-w-0">
                      <p className="font-mono text-sm font-semibold text-foreground">
                        {formatDepositAmount(deposit, activeLanguage)}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatDepositDate(deposit.created_at, activeLanguage)}
                      </p>
                    </div>
                    <ColocaBadge variant={TONE_BADGE_VARIANT[tone]} className="shrink-0 text-[10px]">
                      {toneLabel[tone]}
                    </ColocaBadge>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
